/**
 * IndexedDB-backed image store for custom POI hero images.
 *
 * Why IndexedDB and not localStorage:
 *   - localStorage caps at ~5 MB per origin; one 1600px JPEG fills a meaningful
 *     chunk of that quota, so we'd hit QuotaExceededError after a handful of
 *     custom POIs.
 *   - IndexedDB allows substantially more (50 MB+ by default) and is the
 *     standard browser primitive for blob-ish content.
 *
 * Schema: single object store `images` keyed by POI id (string), value =
 * JPEG-encoded dataURL (string). DataURLs (rather than Blobs) keep the render
 * path trivial: <img src={dataURL} /> works directly.
 */

const DB_NAME = 'map-lens-custom-poi';
const STORE = 'images';
const VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

export async function putCustomPOIImage(id: string, dataURL: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(dataURL, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteCustomPOIImage(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadAllCustomPOIImages(): Promise<Record<string, string>> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const result: Record<string, string> = {};
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        if (typeof cursor.key === 'string' && typeof cursor.value === 'string') {
          result[cursor.key] = cursor.value;
        }
        cursor.continue();
      } else {
        resolve(result);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Resize a user-uploaded image to a JPEG dataURL, capped at the given edge
 * length. Quality is fixed at 0.85, a good middle for photos.
 *
 * Throws if the file isn't a recognisable image.
 */
export async function fileToResizedDataURL(
  file: File,
  maxEdge = 1600,
  quality = 0.85,
): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Could not decode image'));
      i.src = url;
    });
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}
